/*
 * field-types.js — THE field-type registry. Three deployed registries unified
 * into one coherent map `type -> { component, default, validation, decode, encode, … }`:
 *
 *   (1) entity-field-types.js  — the 19 entity field types (Pattern B core, `E`)
 *   (2) schema-field-type-registry.js — device-settings VJsf widget registry
 *       (x-display -> custom Vuetify widget) + built-in displays + schema types
 *   (3) form-builder `fieldsSettings` — the 10 drag-drop form field types
 *
 * Reconstructed from lacak.io deployed behavior; app-specific contract verbatim
 * (every registry KEY, `icon` string, numeric columnWidth/length-cap/maxFields,
 * canBeAdded flag, the $t() i18n keys, the x-display custom-widget names, and the
 * 10 form-builder type keys are re-greppable in the shipped chunks), internal
 * impl re-authored.
 *
 * The three registries share ONE shape: a string `type`/`x-display` key selects
 * a widget component, validation is compiled from declared constraints, and the
 * default value is a thunk. This is SUBSYSTEMS Pattern A ("a field-type registry
 * maps a declarative type to a widget component").
 */

import { v, email, phone, decimalPrecision, location, required, toRule } from './validation.js';
import { t } from './i18n.js';

/* ============================================================================
 * (1) ENTITY FIELD TYPES — exported factory `entityFieldTypes()` (recovered `E`).
 * Returns a FRESH registry object each call so labels re-translate per locale.
 * 19 types: 9 canBeAdded (builder palette) + 10 bundled/built-in.
 * ==========================================================================*/

/** identity decode/encode reused by non-reference types */
const idDecode = (value) => value;
const idEncode = (value) => value;

export function entityFieldTypes() {
  return {
    /* ---- 9 BUILDER-PALETTE TYPES (canBeAdded:true) ------------------------ */
    text: {
      label: t('entities.builder.field_types.text'),
      validation: v.string.and(v.length(0, 700)).or(v.equal(null)).or(v.equal('')),
      default: () => '',
      icon: 'text_format',
      canBeAdded: true,
      columnWidth: 250,
      component: 'entity-input-text',
      decode: idDecode,
      encode: idEncode,
    },
    bigtext: {
      label: t('entities.builder.field_types.bigtext'),
      validation: v.string.and(v.length(0, 20000)).or(v.equal(null)).or(v.equal('')),
      default: () => '',
      icon: 'subject',
      canBeAdded: true,
      columnWidth: 400,
      component: 'entity-input-bigtext',
      decode: idDecode,
      encode: idEncode,
    },
    email: {
      label: t('entities.builder.field_types.email'),
      validation: email().and(v.length(0, 100)).or(v.equal(null)).or(v.equal('')),
      default: () => '',
      icon: 'email',
      canBeAdded: true,
      columnWidth: 200,
      component: 'entity-input-email',
      decode: idDecode,
      encode: idEncode,
    },
    phone: {
      label: t('entities.builder.field_types.phone'),
      validation: phone().or(v.equal(null)),
      default: () => null,
      icon: 'local_phone',
      canBeAdded: true,
      columnWidth: 150,
      component: 'entity-input-phone',
      decode: idDecode,
      encode: idEncode,
    },
    decimal: {
      label: t('entities.builder.field_types.decimal'),
      validation: v.numeric.and(v.between(-99999999999, 99999999999)).and(decimalPrecision(12, 6)).or(v.equal(null)),
      default: () => null,
      icon: 'custom-icon-decimal',
      canBeAdded: true,
      columnWidth: 150,
      component: 'entity-input-decimal',
      decode: idDecode,
      encode: idEncode,
    },
    integer: {
      label: t('entities.builder.field_types.integer'),
      validation: v.integer.and(v.between(-99999999999, 99999999999)).or(v.equal(null)),
      default: () => null,
      icon: 'looks_two',
      canBeAdded: true,
      columnWidth: 150,
      component: 'entity-input-integer',
      decode: idDecode,
      encode: idEncode,
    },
    employee: {
      label: t('entities.builder.field_types.employee'),
      validation: v.integer.or(v.equal(null)),
      default: () => null,
      icon: 'person',
      canBeAdded: true,
      columnWidth: 250,
      params: () => ({ responsible: false }), // "responsible employee" flag
      removeMessage: t('entities.builder.employee-remove-msg'),
      component: 'entity-input-employee',
      // reference type: keep only ids that still resolve against the employee list
      decode: (value, ctx) => (ctx && ctx.employeeById && value != null && !ctx.employeeById[value] ? null : value),
      encode: idEncode,
    },
    image: {
      label: t('entities.builder.field_types.image'),
      validation: v.array,
      default: () => [],
      icon: 'custom-icon-image-outline',
      sortable: false,
      canBeAdded: true,
      columnWidth: 200,
      maxFields: 16,
      component: 'entity-input-image',
      // intersection with the item's known file ids
      decode: (value, ctx) => intersectIds(value, ctx && ctx.item && ctx.item.files),
      encode: idEncode,
    },
    file: {
      label: t('entities.builder.field_types.file'),
      validation: v.array,
      default: () => [],
      icon: 'attach_file',
      canBeAdded: true,
      columnWidth: 200,
      sortable: false,
      params: () => ({ allowed_extensions: null }),
      maxFields: 16,
      component: 'entity-input-file',
      decode: (value, ctx) => intersectIds(value, ctx && ctx.item && ctx.item.files),
      encode: idEncode,
    },

    /* ---- 10 BUILT-IN / BUNDLED TYPES (no canBeAdded) ---------------------- */
    multi_employee: {
      // NB: no `label` in source — supplied by the host entity
      validation: v.array,
      default: () => [],
      icon: 'group',
      columnWidth: 250,
      sortable: false,
      removeMessage: t('entities.builder.employee-remove-msg'),
      component: 'entity-input-multi-employee',
      decode: (value, ctx) => intersectIds(value, ctx && ctx.employeeList && ctx.employeeList.map((e) => e.id)),
      encode: idEncode,
    },
    tags: {
      validation: v.array,
      default: () => [],
      icon: 'custom-icon-hashtag',
      columnWidth: 300,
      sortable: false,
      component: 'entity-input-tags',
      decode: idDecode,
      encode: idEncode,
    },
    location: {
      validation: location,
      default: () => null,
      icon: 'location_on',
      columnWidth: 300,
      component: 'entity-input-location',
      decode: idDecode,
      encode: idEncode,
    },
    password: {
      label: t('entities.builder.field_types.password'),
      default: () => '',
      icon: 'password',
      sortable: false,
      columnWidth: 200,
      component: 'entity-input-password',
      decode: idDecode,
      encode: idEncode,
    },
    date: {
      validation: v.string,
      default: () => null,
      icon: 'event',
      columnWidth: 150,
      component: 'entity-input-date',
      decode: idDecode,
      encode: idEncode,
    },
    datetime: {
      icon: 'access_time',
      columnWidth: 200,
      default: () => null,
      component: 'entity-input-datetime',
      decode: idDecode,
      encode: idEncode,
    },
    tracker: {
      icon: 'custom-icon-tracker',
      columnWidth: 200,
      default: () => null,
      component: 'entity-input-tracker',
      decode: idDecode,
      encode: idEncode,
    },
    select: {
      icon: 'custom-icon-list_bulleted_type',
      columnWidth: 200,
      default: () => null,
      component: 'entity-input-select',
      decode: idDecode,
      encode: idEncode,
    },
    form: {
      icon: 'custom-icon-sticky_note_2',
      columnWidth: 200,
      default: () => null,
      component: 'entity-input-form',
      decode: idDecode,
      encode: idEncode,
    },
    switch: {
      icon: 'toggle_on',
      columnWidth: 200,
      default: () => false,
      component: 'entity-input-switch',
      decode: idDecode,
      encode: idEncode,
    },
  };
}
export { entityFieldTypes as E };

/** keep only array values present in `known` (reference-type decode helper) */
function intersectIds(value, known) {
  if (!Array.isArray(value)) return value;
  if (!Array.isArray(known)) return value;
  const set = new Set(known.map((k) => (k && k.id != null ? k.id : k)));
  return value.filter((x) => set.has(x && x.id != null ? x.id : x));
}

/** ordered list of the 19 type keys */
export const ENTITY_FIELD_TYPES = Object.keys(entityFieldTypes());

/** the 9 builder-palette types (the `fieldTypes` getter filters canBeAdded) */
export function builderPaletteTypes() {
  const reg = entityFieldTypes();
  return Object.keys(reg).filter((k) => reg[k].canBeAdded);
}

/** definition accessor for a single type (fresh registry each call) */
export function fieldTypeDef(type) {
  return entityFieldTypes()[type] || null;
}

/* ---- per-type UI-triad component-name resolvers (kebab, resolved at render) --
 * INPUT "entity-input-<t>" · PRESENTER "entity-presenter-<t>" ·
 * FILTER "entity-filter-<t>" · BUILDER "entity-field-<t>".  A field descriptor
 * may override input/filter/presenter with its own component reference. */
function kebab(type) { return String(type).replace(/_/g, '-'); }
export function inputComponentName(type) { return `entity-input-${kebab(type)}`; }
export function presenterComponentName(type) { return `entity-presenter-${kebab(type)}`; }
export function filterComponentName(type) { return `entity-filter-${kebab(type)}`; }
export function builderComponentName(type) { return `entity-field-${kebab(type)}`; }

/**
 * fieldValidation(field) — the effective Rule for a field descriptor:
 *   field.validationRules || registry[type].validation, ANDed with `required`
 *   when field.required. (Single source shared by model-factory + EntityForm.)
 */
export function fieldValidation(field) {
  const def = fieldTypeDef(field && field.type) || {};
  let rule = field && field.validationRules ? toRule(field.validationRules) : def.validation || null;
  if (field && field.required) rule = rule ? required.and(rule) : required;
  return rule;
}

/** Vuetify `:rules` array for a field (empty array if no rule). */
export function fieldRules(field) {
  const rule = fieldValidation(field);
  return rule ? rule.toRules() : [];
}

/** default value thunk resolution: params.defaultValue -> registry default(). */
export function fieldDefault(field, storeGetters) {
  const params = (field && field.params) || {};
  if (params.defaultValue !== undefined) return params.defaultValue;
  if (params.defaultValueGetter && storeGetters && storeGetters[params.defaultValueGetter] !== undefined) {
    return storeGetters[params.defaultValueGetter];
  }
  const def = fieldTypeDef(field && field.type);
  return def && typeof def.default === 'function' ? def.default() : null;
}

/* ============================================================================
 * (2) DEVICE-SETTINGS SCHEMA WIDGET REGISTRY (VJsf layer). x-display -> widget.
 * The engine mechanics (resultSchema normalization, x-if evaluation, the
 * JSON-Schema -> Vuetify rule compiler) live in ./schema-engine.js; this file
 * owns the *registry* (the type/x-display -> component map), matching (1).
 * ==========================================================================*/

/** x-display value ("custom-*") -> custom scoped-slot / Vuetify widget name. */
export const CUSTOM_WIDGET_REGISTRY = {
  'custom-units-field': 'schema-units-field',
  'custom-slider': 'schema-slider',
  'custom-subtitle': 'schema-subtitle',
  'custom-text-hint': 'schema-text-hint',
  'custom-time-field': 'schema-time-field',
  'custom-radio': 'schema-radio',
  'custom-complex-interval': 'schema-complex-interval',
  'custom-file-field': 'schema-file-field',
  'custom-inverted-checkbox': 'schema-inverted-checkbox',
};

/** x-display values handled by VJsf core (not by a custom slot). */
export const BUILTIN_DISPLAYS = [
  'hidden', 'file', 'markdown', 'combobox', 'radio', 'slider', 'time-field',
  'expansion-panels', 'tabs', 'list', 'section',
];

/** JSON-Schema `type` -> default widget family (VJsf core inference). */
export const SCHEMA_TYPES = ['string', 'number', 'integer', 'boolean', 'object', 'array'];

/**
 * schemaWidgetName(node) — pick a widget for a JSON-Schema node.
 *   x-display custom-* -> custom widget; else map schema type -> an entity input
 *   (the shared-registry fallback SchemaForm.vue uses when VJsf is absent).
 */
export function schemaWidgetName(node) {
  const disp = node && node['x-display'];
  if (disp && String(disp).indexOf('custom-') === 0) return CUSTOM_WIDGET_REGISTRY[disp] || disp;
  const type = node && node.type;
  if (node && (node.enum || node.oneOf)) return 'entity-input-select';
  switch (type) {
    case 'boolean': return 'entity-input-switch';
    case 'number':
    case 'integer': return 'entity-input-integer';
    case 'string':
    default: return 'entity-input-text';
  }
}

/* ============================================================================
 * (3) FORM-BUILDER FIELD TYPES — the 10 drag-drop form field types
 * (Vuex `form-builder` getter `fieldsSettings`). Same shape as (1)/(2): a type
 * key -> { component, default props, validation }. RECOVERED-VERBATIM keys.
 * ==========================================================================*/

export function formBuilderFieldTypes() {
  return {
    text: { component: 'form-field-text', icon: 'text_fields', canBeAdded: true, default: () => ({ label: t('form.field.text'), required: false, value: '' }) },
    checkbox_group: { component: 'form-field-checkbox-group', icon: 'check_box', canBeAdded: true, default: () => ({ label: t('form.field.checkbox_group'), required: false, group: [] }) },
    radio_group: { component: 'form-field-radio-group', icon: 'radio_button_checked', canBeAdded: true, default: () => ({ label: t('form.field.radio_group'), required: false, group: [] }) },
    dropdown: { component: 'form-field-dropdown', icon: 'arrow_drop_down_circle', canBeAdded: true, default: () => ({ label: t('form.field.dropdown'), required: false, options: [] }) },
    date: { component: 'form-field-date', icon: 'event', canBeAdded: true, default: () => ({ label: t('form.field.date'), required: false, value: null }) },
    separator: { component: 'form-field-separator', icon: 'remove', canBeAdded: true, default: () => ({ label: t('form.field.separator') }) },
    rating: { component: 'form-field-rating', icon: 'star', canBeAdded: true, default: () => ({ label: t('form.field.rating'), required: false, max: 5, value: 0 }) },
    file: { component: 'form-field-file', icon: 'attach_file', canBeAdded: true, default: () => ({ label: t('form.field.file'), required: false, files: [] }) },
    photo: { component: 'form-field-photo', icon: 'photo_camera', canBeAdded: true, default: () => ({ label: t('form.field.photo'), required: false, files: [] }) },
    signature: { component: 'form-field-signature', icon: 'gesture', canBeAdded: true, default: () => ({ label: t('form.field.signature'), required: false, value: null }) },
  };
}
export const FORM_BUILDER_FIELD_TYPES = Object.keys(formBuilderFieldTypes());

export default {
  entityFieldTypes,
  ENTITY_FIELD_TYPES,
  builderPaletteTypes,
  fieldTypeDef,
  fieldValidation,
  fieldRules,
  fieldDefault,
  inputComponentName,
  presenterComponentName,
  filterComponentName,
  builderComponentName,
  CUSTOM_WIDGET_REGISTRY,
  BUILTIN_DISPLAYS,
  SCHEMA_TYPES,
  schemaWidgetName,
  formBuilderFieldTypes,
  FORM_BUILDER_FIELD_TYPES,
};
